import { Badge, Divider } from "@material-ui/core"
import AttachFileIcon from "@material-ui/icons/AttachFile"
import CompareArrowsIcon from "@material-ui/icons/CompareArrows"
import CreateIcon from "@material-ui/icons/Create"
import MyLocation from "@material-ui/icons/MyLocation"
import PinDrop from "@material-ui/icons/PinDrop"
import LocationDisabledIcon from "@mui/icons-material/LocationDisabled"
import { createRef, useEffect, useMemo, useState } from "react"
import type { ReactElement, RefObject } from "react"
import { connect } from "react-redux"
import { useHistory } from "react-router-dom"
import type { IConfirmationDialogProps } from ".."
import { ConfirmationDialog } from ".."
import type { AppState, AppThunkDispatch, MapViewerPanelAction, MapViewerPinAction, Project } from "../../../redux"
import { MapViewerPanel, mapViewerSwitchActionPanel, mapViewerTogglePin } from "../../../redux"
import { LeafletBottomLeft } from "../../../utils"
import { DragHandle, FreeDraggable, MapControl, MapControlContainer, TooltipIconButton } from "../../atoms"

import "./map-actions-panel.styles.scss"
import { useTranslation } from "react-i18next"
import { useQuery } from "../../../hooks"
import { useTracker } from "../../../hooks/useTracker"
import { AnalyticsElementModule } from "../../../models"
import { SaveDialog } from "../save-dialog/save-dialog.component"

export interface IMapActionOptions {
  googleMaps?: boolean
  comparisons?: boolean
  drawing?: boolean
  attachments?: boolean
  sharedProject?: boolean
}

export interface IMapActionsPanelOwnProps {
  attachmentCount: number
  options?: IMapActionOptions
  mapId?: string
  projectTitle?: string
}

export interface IMapActionsPanelStateProps {
  isProjectDirty: boolean
  pins: Map<string, boolean>
  panels: Map<MapViewerPanel, boolean>
  project?: Project
  togglePanel: (panel: MapViewerPanel) => void
  togglePin: (mapId: string, active?: boolean) => void
}

function MapActionsPanelView(props: IMapActionsPanelOwnProps & IMapActionsPanelStateProps): ReactElement {
  const { mapId, panels, pins, options, attachmentCount, isProjectDirty, togglePanel, togglePin, projectTitle } = props
  const tracker = useTracker()
  const { t } = useTranslation(["general", "map", "project"])
  const queryParams = useQuery()

  const dragHandleRef: RefObject<HTMLDivElement> = createRef<HTMLDivElement>()

  const [confirmDialogProps, setConfirmDialogProps] = useState<IConfirmationDialogProps>({
    open: false,
    title: t("map:actionButtons.comparison.makeComparison"),
    bodyText: (
      <>
        <b>{t("map:actionButtons.comparison.notYetSavedComparison")}</b>
        <br />
        {t("map:actionButtons.comparison.notYetSavedComparisonText")}
      </>
    ),
    onCancel: closeConfirmationDialog,
    onConfirm: () => {},
    confirmText: t("general:save")
  })

  const [showWarning, setShowWarning] = useState<boolean>(false)
  const [activePanel, setActivePanel] = useState<MapViewerPanel>()
  const [showSaveDialog, setShowSaveDialog] = useState<boolean>(false)

  const showAttachmentBadge: boolean = attachmentCount > 0 && !(panels.get(MapViewerPanel.Attachement) || false)

  function optionalToBoolean(option?: boolean): boolean {
    return option === undefined ? true : option
  }

  const history = useHistory()

  const showGoogleMaps: boolean = optionalToBoolean(options?.googleMaps)
  const showComparisons: boolean = optionalToBoolean(options?.comparisons)
  const showDrawing: boolean = optionalToBoolean(options?.drawing)
  const showAttachments: boolean = optionalToBoolean(options?.attachments)
  const sharedProject: boolean = optionalToBoolean(options?.sharedProject)
  const showTopographicalPin: boolean = useMemo(() => {
    return queryParams.has("name") && queryParams.has("lat") && queryParams.has("lng")
  }, [queryParams])

  const googleMapsActive: boolean | undefined = useMemo(() => {
    return panels.get(MapViewerPanel.GoogleMaps)
  }, [panels])

  const pinActive: boolean | undefined = useMemo(() => {
    return mapId ? pins.get(mapId) : undefined
  }, [mapId, pins])

  function handleComparisonClicked(): void {
    trackMapActionItemClicked("comparison")
    if (isProjectDirty) {
      setConfirmDialogProps({
        ...confirmDialogProps,
        open: true,
        onConfirm: () => {
          handleSave()
        }
      })
    } else {
      history.replace(`/map/${mapId}/comparison`)
    }
  }

  function trackMapActionItemClicked(itemId: string): void {
    tracker.elementClicked({
      module: AnalyticsElementModule.MapActionsPanel,
      itemId,
      type: "button",
      elementId: "map-action"
    })
  }

  function handlePanelToggleClick(panel?: MapViewerPanel): void {
    if (panel === undefined) return
    setActivePanel(panel)

    if (
      sharedProject &&
      showWarning === false &&
      (panel === MapViewerPanel.Attachement || panel === MapViewerPanel.Drawing)
    ) {
      setShowWarning(true)
      return
    }
    trackMapActionItemClicked(getPanelName(panel))
    togglePanel(panel)
  }

  function handlePinToggleClick(): void {
    if (!mapId) return
    trackMapActionItemClicked("topographical-pin-toggle")
    togglePin(mapId)
  }

  function getPanelName(panel: MapViewerPanel): string {
    switch (panel) {
      case MapViewerPanel.Attachement:
        return "attachment"
      case MapViewerPanel.GoogleMaps:
        return "google-maps"
      case MapViewerPanel.Drawing:
        return "drawing"
      default:
        throw new Error("Unknown panel")
    }
  }

  function closeConfirmationDialog(): void {
    setConfirmDialogProps({ ...confirmDialogProps, open: false })
  }

  function handleSave(): void {
    setShowSaveDialog(true)
    closeConfirmationDialog()
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (!showWarning) return

    setConfirmDialogProps({
      ...confirmDialogProps,
      open: true,
      title: t("project:warningEditSharedMap.warningSharedMap"),
      bodyText: <>{t("project:warningEditSharedMap.warningEditSave")}</>,
      onConfirm: () => {
        closeConfirmationDialog()
        setShowWarning(false)
        handlePanelToggleClick(activePanel)
      },
      onCancel: () => {
        closeConfirmationDialog()
        setShowWarning(false)
      }
    })
  }, [showWarning])
  useEffect(() => {
    if (!mapId) return
    togglePin(mapId, showTopographicalPin)
  }, [mapId, showTopographicalPin, togglePin])

  return (
    <>
      <ConfirmationDialog {...confirmDialogProps} />
      {showSaveDialog && (
        <SaveDialog mapId={mapId} projectTitle={projectTitle} closeSaveDialog={() => setShowSaveDialog(false)} />
      )}
      <MapControlContainer position={LeafletBottomLeft} className="map-actions-panel">
        <FreeDraggable handleRef={dragHandleRef}>
          <MapControl>
            <DragHandle ref={dragHandleRef} />
            <Divider orientation="vertical" />
            {showGoogleMaps && (
              <TooltipIconButton
                onClick={() => handlePanelToggleClick(MapViewerPanel.GoogleMaps)}
                selected={googleMapsActive}
                placement="top"
                tooltip="Google Maps"
                data-testid="panel-toggle-map"
                icon={<PinDrop />}
              />
            )}
            {showComparisons && (
              <TooltipIconButton
                onClick={handleComparisonClicked}
                disabled={googleMapsActive}
                placement="top"
                tooltip={t("map:actionButtons.compare")}
                icon={<CompareArrowsIcon />}
              />
            )}

            {showDrawing && (
              <TooltipIconButton
                onClick={() => handlePanelToggleClick(MapViewerPanel.Drawing)}
                disabled={googleMapsActive}
                selected={panels.get(MapViewerPanel.Drawing)}
                placement="top"
                tooltip={t("map:actionButtons.edit.tooltip")}
                icon={<CreateIcon />}
              />
            )}
            {showAttachments && (
              <TooltipIconButton
                onClick={() => handlePanelToggleClick(MapViewerPanel.Attachement)}
                selected={panels.get(MapViewerPanel.Attachement)}
                disabled={googleMapsActive}
                placement="top"
                tooltip={t("map:actionButtons.attachmentTooltip")}
                icon={
                  <Badge badgeContent={showAttachmentBadge ? attachmentCount : undefined} color="secondary">
                    <AttachFileIcon />
                  </Badge>
                }
              />
            )}
            {showTopographicalPin && (
              <TooltipIconButton
                onClick={handlePinToggleClick}
                disabled={googleMapsActive}
                placement="top"
                tooltip={t(
                  pinActive
                    ? "map:actionButtons.topographicalPin.hideTooltip"
                    : "map:actionButtons.topographicalPin.showTooltip"
                )}
                icon={<Badge color="secondary">{pinActive ? <LocationDisabledIcon /> : <MyLocation />}</Badge>}
              />
            )}
          </MapControl>
        </FreeDraggable>
      </MapControlContainer>
    </>
  )
}

function mapStateToProps(state: AppState) {
  return {
    pins: state.mapViewer.pins,
    panels: state.mapViewer.panels,
    isProjectDirty: state.project.present.isDirty
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<MapViewerPanelAction | MapViewerPinAction>) {
  return {
    togglePanel: (panel: MapViewerPanel) => dispatch(mapViewerSwitchActionPanel(panel)),
    togglePin: (mapId: string, active?: boolean) => dispatch(mapViewerTogglePin(mapId, active))
  }
}

export const MapActionsPanel = connect(mapStateToProps, mapDispatchToProps)(MapActionsPanelView)
